<template>
    <div :class="['table-container', { 'no-shadow': disableShadow }]">
        <Spinner v-if="loading" class="state-container" display-text="Loading data..." />

        <DisplayState v-else-if="emptyMessage"
            class="state-container"
            :icon-name="iconNames.empty"
            :state-text="emptyMessage"
            state-sub-text=""
        />

        <DisplayState v-else-if="error"
            class="state-container"
            iconName="error"
            stateText="Something went wrong"
            :stateSubText="errorMessage"
        />

        <DisplayState v-else-if="!visibleColumns.length && !disableColumnDropdown" 
            class="state-container" 
            :icon-name="iconNames.empty"
            state-text="No columns visible" 
            state-sub-text="Please select at least one column to display" 
        />

        <DisplayState v-else-if="!filteredRows.length" 
            class="state-container" 
            :icon-name="iconNames.closeBox"
            state-text="No data to display" 
            state-sub-text="Try adjusting your filters or search criteria" 
        />

        <div v-else :class="['scroll-inner', { scrollable }]">
            <ModernTable
                class="table-wrapper"
                :sort-config="sortConfig"
                :sorted-and-filtered-rows="sortedAndFilteredRows"
                :actions="actions"
                :visible-columns="visibleColumns"
                :editable-columns="editableColumns"
                :editable-column-types="editableColumnTypes"
                :row-editable-state="rowEditableState"
                :toggle-columns="toggleColumns"
                :badge-columns="badgeColumns"
            />
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { iconNames } from '@/Modules/utils/icons';
import Spinner from './Spinner.vue';
import DisplayState from './DisplayState.vue';
import ModernTable from './ModernTable.vue';

import { useTable } from '@/Modules/Automationer/useTable';

const props = defineProps({
    rows: {
        type: Array,
        default: () => []
    },
    searchQuery: {
        type: String,
        default: ''
    },
    searchOnColumn: {
        type: String,
        required: true
    },
    filters: {
        type: Array,
        default: () => []
    },
    columnVisibility: {
        type: Array,
        default: () => []
    },
    actions: {
        type: [Array, Function],
        default: () => []
        // Expected format:
        // [
        //   {
        //     type: 'button', // 'button' or 'row-click' (default: 'button')
        //     label: 'Details', // button text if no icon (for button type)
        //     icon: 'eye', // predefined icon name (for button type)
        //     variant: 'primary', // styling variant (for button type)
        //     tooltip: 'View Details', // tooltip text (for button type)
        //     handler: (row) => { /* your logic */ }, // function to call
        //   },
        //   {
        //     type: 'row-click', // makes entire row clickable
        //     handler: (row) => { /* your logic */ }, handler function called on click
        //   }
        // ]
    },
    scrollable: {
        type: Boolean,
        default: false
    },
    disableShadow: {
        type: Boolean,
        default: false
    },
    editableColumns: {
        type: Array,
        default: () => []
    },
    editableColumnTypes: {
        type: Object,
        default: () => ({})
    },
    disableColumnDropdown: {
        type: Boolean,
        default: false
    },
    ignoredKeys: {
        type: Set,
        required: false
    },
    error: {
        type: Boolean,
        default: false
    },
    errorMessage: {
        type: String,
        default: 'No data available or failed to load.'
    },
    emptyMessage: {
        type: String,
        default: ''
    },
    loading: {
        type: Boolean,
        default: false
    },
    rowEditableState: {
        type: Object,
        default: () => ({})
    },
    toggleColumns: {
        type: Object,
        default: () => ({})
    },
    badgeColumns: {
        type: Object,
        default: () => ({})
    }
})
const sortConfig = ref({
    column: null,
    direction: null // 'asc', 'desc', or null
})

const {
  filteredRows,
  sortedAndFilteredRows,
  visibleColumns
} = useTable(props, sortConfig)

</script>

<style scoped>
.table-container {
    width: 100%;
    border-radius: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1), 0 8px 16px rgba(0, 0, 0, 0.08);
    border: 1px solid rgba(0, 0, 0, 0.05);
    backdrop-filter: blur(20px);
    background: rgba(255, 255, 255, 0.95);
    position: relative;
    z-index: 2;
}

.no-shadow {
  box-shadow: none !important;
  border: none; /* Optional: remove border if it's tied to shadowed look */
}


.state-container {
    
    justify-content: center;
    padding: 48px 32px;
    gap: 16px;
}

.table-wrapper {
    overflow: visible;
    border-radius: 16px;
}

.state-container {
    animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(8px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.scroll-inner {
  max-height: none;
  overflow: visible;
}

.scroll-inner.scrollable {
  max-height: 500px; /* or pass via prop if you want it dynamic */
  overflow-y: auto;
  overflow-x: hidden;
  background: white;
  padding-right: 4px; /* optional: avoids content getting hidden under scrollbar */
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
}

</style>

